import { SkaterInfo, SubmittedWaiver } from '@types';
import { PDFViewerModal } from '_components/atoms';
import UserService from '_services/UserService';
import HelperMethods from '_utils/HelperMethods';
import React, { useEffect, useState } from 'react';

interface MySubmittedWaiversListProps {
  onAttach: (waiver: SubmittedWaiver, skaterId?: string) => void;
  onUnattach?: (skaterId?: string) => void;
  attachedWaiverIds?: Record<string, number>; // firstName_lastName -> waiverId mapping
  skatersInfo?: SkaterInfo[]; // Add skatersInfo prop
}

const MySubmittedWaiversList: React.FC<MySubmittedWaiversListProps> = ({
  onAttach,
  onUnattach,
  attachedWaiverIds = {},
  skatersInfo = [],
}) => {
  const [waivers, setWaivers] = useState<SubmittedWaiver[]>([]);
  const [loading, setLoading] = useState(true);
  const [selectedSkaterId, setSelectedSkaterId] = useState<string | null>(null);
  const [pdfViewerModal, setPdfViewerModal] = useState<{
    isOpen: boolean;
    url: string;
    title: string;
  }>({
    isOpen: false,
    url: '',
    title: '',
  });

  const fetchWaivers = async () => {
    try {
      setLoading(true);

      const responseData =
        (await UserService.getCustomerWaivers()) as unknown as SubmittedWaiver[];

      if (responseData) {
        setWaivers(responseData);
      }
    } catch (error) {
      console.error('Error fetching waivers:', error);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchWaivers();
  }, []);

  const openPdfViewer = (url: string, title: string) => {
    setPdfViewerModal({
      isOpen: true,
      url,
      title,
    });
  };

  const closePdfViewer = () => {
    setPdfViewerModal({
      isOpen: false,
      url: '',
      title: '',
    });
  };

  return (
    <div className="space-y-4">
      {/* Skater Selector */}
      {skatersInfo.length > 0 && (
        <div className="bg-white border border-gray-200 rounded-lg p-4">
          <label className="block text-md font-bold text-gray-700 mb-2">
            Select Skater to Attach Waiver
          </label>
          <div className="flex flex-wrap gap-2">
            {skatersInfo.map(skater => (
              <button
                key={skater.id}
                onClick={() => setSelectedSkaterId(skater.id)}
                className={`px-3 py-2 rounded-md text-sm border transition-colors ${
                  selectedSkaterId === skater.id
                    ? 'bg-blue-600 text-white border-blue-600'
                    : 'bg-gray-50 text-gray-700 border-gray-300 hover:bg-gray-100'
                }`}
              >
                {skater.firstName} {skater.lastName}
              </button>
            ))}
          </div>
        </div>
      )}

      {/* Show currently attached waivers */}
      {Object.keys(attachedWaiverIds).length > 0 && (
        <div className="bg-green-50 border border-green-200 rounded-lg p-4">
          <h4 className="text-sm font-medium text-green-800 mb-2">
            Attached Waivers:
          </h4>
          <div className="space-y-1">
            {Object.entries(attachedWaiverIds).map(([skaterKey, waiverId]) => {
              // Find skater by firstName_lastName key
              const skater = skatersInfo.find(
                s => `${s.firstName}_${s.lastName}` === skaterKey,
              );
              const waiver = waivers.find(w => w.id === waiverId);
              if (!skater || !waiver) return null;
              return (
                <div
                  key={skaterKey}
                  className="flex items-center justify-between text-sm"
                >
                  <span className="text-green-700">
                    <strong>
                      {skater.firstName} {skater.lastName}
                    </strong>{' '}
                    → {waiver.skaterName}
                  </span>
                  <button
                    onClick={() => onUnattach?.(skaterKey)}
                    className="text-red-600 hover:text-red-800 text-xs"
                  >
                    Remove
                  </button>
                </div>
              );
            })}
          </div>
        </div>
      )}

      {/* Waivers Table - Desktop */}
      <div className="hidden md:block bg-white rounded-lg shadow-sm border border-gray-200 overflow-hidden">
        <div className="overflow-x-auto">
          <table className="min-w-full divide-y divide-gray-200">
            <thead className="bg-gray-50">
              <tr>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Skater Info
                </th>
                <th className="px-6 py-3 text-center text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Actions
                </th>
              </tr>
            </thead>
            <tbody className="bg-white divide-y divide-gray-200">
              {waivers.length === 0 ? (
                <tr>
                  <td
                    colSpan={2}
                    className="px-6 py-12 text-center text-gray-500"
                  >
                    {loading ? 'Loading waivers...' : 'No waivers found'}
                  </td>
                </tr>
              ) : (
                waivers.map(waiver => {
                  const attachedSkaterKey = Object.entries(
                    attachedWaiverIds,
                  ).find(([_, wid]) => wid === waiver.id)?.[0];
                  const isAttachedToAnySkater = attachedSkaterKey !== undefined;

                  // Compare using firstName_lastName keys
                  const selectedSkater = selectedSkaterId
                    ? skatersInfo.find(s => s.id === selectedSkaterId)
                    : null;
                  const selectedSkaterKey = selectedSkater
                    ? `${selectedSkater.firstName}_${selectedSkater.lastName}`
                    : null;
                  const isAttachedToSelectedSkater =
                    selectedSkaterKey &&
                    attachedSkaterKey === selectedSkaterKey;
                  const shouldHighlight = selectedSkaterId
                    ? isAttachedToSelectedSkater
                    : isAttachedToAnySkater;

                  return (
                    <tr
                      key={waiver.id}
                      className={`hover:bg-gray-50 ${shouldHighlight ? 'bg-blue-50' : ''}`}
                    >
                      <td className="px-6 py-4">
                        <div className="flex items-center">
                          <div className="flex-1">
                            <div className="text-sm font-medium text-gray-900">
                              {waiver.skaterName}
                            </div>
                            <div className="text-sm text-gray-500">
                              DOB:{' '}
                              {HelperMethods.formatDate(waiver.dateOfBirth)}
                            </div>
                            <div className="text-xs text-gray-400">
                              {waiver.isAdult ? 'Adult' : 'Minor'}
                            </div>
                            <div className="text-xs text-gray-400">
                              Submitted:{' '}
                              {HelperMethods.formatDateTimeLocal(
                                waiver.submittedAt,
                              )}
                            </div>
                          </div>
                          {attachedSkaterKey && (
                            <div className="ml-3">
                              <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-green-100 text-green-800">
                                Attached
                              </span>
                            </div>
                          )}
                        </div>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm font-medium">
                        <div className="flex space-x-3 justify-center">
                          {waiver.pdfFilePath && (
                            <button
                              onClick={() =>
                                openPdfViewer(
                                  waiver.pdfFilePath,
                                  `Waiver - ${waiver.skaterName}`,
                                )
                              }
                              className="text-blue-600 hover:text-blue-900 transition-colors"
                            >
                              View Waiver
                            </button>
                          )}
                          {!selectedSkaterId ? (
                            <span className="text-gray-500 text-xs">
                              Select a skater
                            </span>
                          ) : (
                            (() => {
                              const selectedSkater = skatersInfo.find(
                                s => s.id === selectedSkaterId,
                              );
                              const selectedSkaterKey = selectedSkater
                                ? `${selectedSkater.firstName}_${selectedSkater.lastName}`
                                : null;

                              return isAttachedToSelectedSkater ? (
                                <button
                                  onClick={() =>
                                    onUnattach?.(selectedSkaterKey!)
                                  }
                                  className="text-red-600 hover:text-red-900 transition-colors"
                                >
                                  Unattach
                                </button>
                              ) : (
                                <button
                                  onClick={() =>
                                    onAttach(waiver, selectedSkaterId)
                                  }
                                  className="text-green-600 hover:text-green-900 transition-colors"
                                >
                                  Attach
                                </button>
                              );
                            })()
                          )}
                        </div>
                      </td>
                    </tr>
                  );
                })
              )}
            </tbody>
          </table>
        </div>
      </div>

      {/* Waivers Cards - Mobile */}
      <div className="md:hidden space-y-4">
        {waivers.length === 0 ? (
          <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6 text-center text-gray-500">
            {loading ? 'Loading waivers...' : 'No waivers found'}
          </div>
        ) : (
          waivers.map(waiver => {
            const attachedSkaterKey = Object.entries(attachedWaiverIds).find(
              ([_, wid]) => wid === waiver.id,
            )?.[0];
            const isAttachedToAnySkater = attachedSkaterKey !== undefined;

            // Compare using firstName_lastName keys
            const selectedSkater = selectedSkaterId
              ? skatersInfo.find(s => s.id === selectedSkaterId)
              : null;
            const selectedSkaterKey = selectedSkater
              ? `${selectedSkater.firstName}_${selectedSkater.lastName}`
              : null;
            const isAttachedToSelectedSkater =
              selectedSkaterKey && attachedSkaterKey === selectedSkaterKey;
            const shouldHighlight = selectedSkaterId
              ? isAttachedToSelectedSkater
              : isAttachedToAnySkater;

            return (
              <div
                key={waiver.id}
                className={`bg-white rounded-lg shadow-sm border border-gray-200 p-4 ${
                  shouldHighlight ? 'ring-2 ring-blue-500' : ''
                }`}
              >
                {/* Skater Info */}
                <div className="mb-4">
                  <div className="flex items-center justify-between mb-2">
                    <h3 className="text-lg font-semibold text-gray-900">
                      {waiver.skaterName}
                    </h3>
                    {attachedSkaterKey && (
                      <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-green-100 text-green-800">
                        Attached
                      </span>
                    )}
                  </div>
                  <div className="text-sm text-gray-600">
                    DOB: {HelperMethods.formatDate(waiver.dateOfBirth)}
                  </div>
                  <div className="text-xs text-gray-400">
                    {waiver.isAdult ? 'Adult' : 'Minor'}
                  </div>
                  <div className="text-xs text-gray-400">
                    Submitted:{' '}
                    {HelperMethods.formatDateTimeLocal(waiver.submittedAt)}
                  </div>
                </div>

                {/* Actions */}
                <div className="flex flex-wrap gap-2">
                  {waiver.pdfFilePath && (
                    <button
                      onClick={() =>
                        openPdfViewer(
                          waiver.pdfFilePath,
                          `Waiver - ${waiver.skaterName}`,
                        )
                      }
                      className="inline-flex items-center px-3 py-2 bg-blue-100 text-blue-700 text-sm rounded-md hover:bg-blue-200 transition-colors"
                    >
                      View Waiver
                    </button>
                  )}
                  {!selectedSkaterId ? (
                    <span className="text-gray-500 text-xs">
                      Select a skater above
                    </span>
                  ) : (
                    (() => {
                      const selectedSkater = skatersInfo.find(
                        s => s.id === selectedSkaterId,
                      );
                      const selectedSkaterKey = selectedSkater
                        ? `${selectedSkater.firstName}_${selectedSkater.lastName}`
                        : null;

                      return isAttachedToSelectedSkater ? (
                        <button
                          onClick={() => onUnattach?.(selectedSkaterKey!)}
                          className="inline-flex items-center px-3 py-2 bg-red-100 text-red-700 text-sm rounded-md hover:bg-red-200 transition-colors"
                        >
                          Unattach
                        </button>
                      ) : (
                        <button
                          onClick={() => onAttach(waiver, selectedSkaterId)}
                          className="inline-flex items-center px-3 py-2 bg-green-100 text-green-700 text-sm rounded-md hover:bg-green-200 transition-colors"
                        >
                          Attach
                        </button>
                      );
                    })()
                  )}
                </div>
              </div>
            );
          })
        )}
      </div>

      {/* PDF Viewer Modal */}
      <PDFViewerModal
        isOpen={pdfViewerModal.isOpen}
        onClose={closePdfViewer}
        url={pdfViewerModal.url}
        title={pdfViewerModal.title}
      />
    </div>
  );
};

export default MySubmittedWaiversList;
